import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  useTheme,
  alpha,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, History } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Import images
import ShieldImage from '../assets/Shield-admin.png'; 
import FeedbackImage from '../assets/feedbacks.jpg'; 
import ReportsImage from '../assets/reports.jpg';
import StatsImage from '../assets/stats.jpg'; 
import ManageAdminsImage from '../assets/ManageAdmins.jpg';

interface DashboardBoxProps {
  title: string;
  path: string;
  isDark: boolean;
  backgroundImage: string;
}

const DashboardBox: React.FC<DashboardBoxProps> = ({ 
  title, 
  path, 
  isDark,
  backgroundImage 
}) => {
  const navigate = useNavigate();
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  return (
    <Grid 
  size={{ xs: 12, sm: 6, md: 4 }}
  sx={{ px: 0.5, py: 0.5 }}
>
      <Card
        onClick={() => navigate(path)}
        sx={{
          height: '380px',
          width: '100%',
          minWidth: { xs: '330px', md: '468px' }, 
          maxWidth: { xs: '100%', md: '400px' }, 
          margin: '0 auto',
          borderRadius: '24px',
          overflow: 'hidden',
          position: 'relative',
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          border: isDark 
            ? '1px solid rgba(196, 182, 111, 0.3)' 
            : '1px solid rgba(5, 24, 20, 0.2)',
          boxShadow: isDark 
            ? '0 20px 60px rgba(0,0,0,0.7)' 
            : '0 20px 60px rgba(5, 24, 20, 0.1)',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: isDark 
              ? '0 30px 80px rgba(196, 182, 111, 0.2)' 
              : '0 30px 80px rgba(5, 24, 20, 0.25)'
          },
          '&:active': {
            transform: 'translateY(-2px)',
          },
        }}
      >
        {/* Dark overlay for better text visibility */}
        <Box
          className="overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDark 
              ? 'rgba(0, 0, 0, 0.5)' 
              : 'rgba(255, 255, 255, 0.2)',
            transition: 'background-color 0.3s ease',
          }}
        />
        
        {/* Content */}
        <CardContent sx={{ 
          p: 3, 
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}>
          {/* Title - Large and centered */}
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              color: isDark ? '#FFFFFF' : deepGreen,
              mb: 1,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontStyle: 'Oblique',
              lineHeight: 1,
              textTransform: 'uppercase',
            }}
          >
            {title}
          </Typography>
          
          {/* Single line below title */}
          <Box
            sx={{
              width: '80px',
              height: '3px',
              backgroundColor: isDark ? goldColor : deepGreen,
              borderRadius: '2px',
            }}
          />
          
          {/* Very small click hint */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: isDark ? goldColor : deepGreen,
              opacity: 0.6,
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
              }}
            >
              CLICK HERE AND SEE THE DATA
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const navigate = useNavigate();
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const dashboardItems = [
    {
      title: 'Feedback',
      path: '/admin/feedback',
      backgroundImage: FeedbackImage,
    },
    {
      title: 'Reports',
      path: '/admin/reports',
      backgroundImage: ReportsImage,
    },
    {
      title: 'Stats',
      path: '/admin/stats',
      backgroundImage: StatsImage,
    },
    {
      title: 'Manage Admins',
      path: '/admin/manage-admins',
      backgroundImage: ManageAdminsImage, // Make sure to add this image
    },
  ];

  if (!user) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: isDark ? '#000000' : '#FAFAFA', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Container maxWidth="sm">
          <Paper sx={{ 
            p: 6, 
            borderRadius: '32px', 
            textAlign: 'center', 
            ...glassEffect 
          }}>
            <History sx={{ fontSize: 80, color: goldColor, mb: 3 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                ...discordFont, 
                color: isDark ? '#FFFFFF' : deepGreen, 
                mb: 2 
              }}
            >
              Admin Access Required
            </Typography>
            <Typography sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', 
              mb: 4 
            }}>
              You need to be logged in as an admin to access this panel.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/login')} 
              sx={{ 
                background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', 
                color: isDark ? '#051814' : '#FFFFFF', 
                borderRadius: '16px', 
                px: 4, 
                py: 1.5, 
                fontWeight: 700, 
                '&:hover': { 
                  background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' 
                } 
              }}
            >
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      overflowX: 'hidden',
    }}>
      <Container maxWidth="xl" sx={{ 
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
      }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')} 
          sx={{ 
            mb: 3, 
            color: goldColor, 
            '&:hover': { 
              background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' 
            } 
          }}
        >
          Back to Home
        </Button>

        {/* Header with Shield Image - Exactly like feedback page */}
        <Paper sx={{ 
          p: 0, 
          mb: 6, 
          borderRadius: '32px', 
          overflow: 'hidden', 
          ...glassEffect, 
          display: 'flex', 
          flexDirection: { xs: 'column-reverse', md: 'row' }, 
          alignItems: 'center',
          textAlign: { xs: 'center', md: 'left' },
        }}>
          <Box sx={{ 
            p: { xs: 3, md: 6 }, 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}>
              <History sx={{ fontSize: { xs: 32, md: 40 }, color: goldColor, mr: 2 }} />
              <Typography variant="h3" sx={{ 
                ...discordFont, 
                background: isDark 
                  ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' 
                  : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`, 
                backgroundClip: 'text', 
                WebkitBackgroundClip: 'text', 
                color: 'transparent', 
                fontSize: { xs: '1.75rem', md: '3rem' },
                textAlign: { xs: 'center', md: 'left' },
              }}>
                Admin Dashboard
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
              fontWeight: 400, 
              maxWidth: 600,
              textAlign: { xs: 'center', md: 'left' },
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}>
              Manage all aspects of the WFHCafe Private Beta from one central location. 
              Access feedback, reports, and analytics to monitor and improve the platform.
            </Typography>
          </Box>
          <Box sx={{ 
            width: { xs: '100%', md: '40%' }, 
            height: { xs: 180, md: '100%' }, 
            minHeight: { md: 300 }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            p: { xs: 2, md: 4 } 
          }}>
            <img 
              src={ShieldImage} 
              alt="Admin Shield" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain', 
                maxHeight: 250 
              }} 
            />
          </Box>
        </Paper>

        {/* Dashboard Boxes Grid - Minimal gap (10px or less) */}
        <Grid 
          container 
          spacing={2} // ~4px gap (10px total with padding)
          sx={{ 
            justifyContent: 'center',
            alignItems: 'stretch',
            mb: 6,
            px: 0.5, // Additional minimal padding
          }}
        >
          {dashboardItems.map((item, index) => (
            <DashboardBox
              key={index}
              title={item.title}
              path={item.path}
              isDark={isDark}
              backgroundImage={item.backgroundImage}
            />
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminPanel;